import React, { useRef } from "react";
import { Controller } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parse, isValid, subYears } from "date-fns";
import { TranslationType } from "../types";
import { cn } from "@/lib/utils";

interface BirthdayFieldProps {
  control: any;
  errors: any;
  t: TranslationType;
  validateBirthday: (value: string) => boolean;
}

export const BirthdayField = ({
  control,
  errors,
  t,
  validateBirthday,
}: BirthdayFieldProps) => {
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    nextRef: React.RefObject<HTMLInputElement> | null,
    maxLength: number,
  ) => {
    const value = e.target.value.replace(/\D/g, "");
    e.target.value = value;

    if (value.length === maxLength && nextRef?.current) {
      nextRef.current.focus();
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="birthday-day">{t.birthday}</Label>
      <Controller
        name="birthday"
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            const date = parse(value, "dd.MM.yyyy", new Date());
            if (!isValid(date)) return t.birthdayInvalid || "Invalid date";

            const today = new Date();
            const minAge = subYears(today, 18);
            const maxAge = subYears(today, 150);

            if (date > today)
              return t.birthdayFuture || "Birthday cannot be in the future";
            if (date > minAge)
              return t.birthdayMinAge || "You must be at least 18 years old";
            if (date < maxAge)
              return (
                t.birthdayMaxAge ||
                "Sorry, longevity science hasn't progressed that much yet!"
              );

            return true;
          },
        }}
        render={({ field: { onChange, value } }) => {
          const parts = value ? value.split(".") : ["", "", ""];

          return (
            <div className="flex gap-2">
              <div className="w-16">
                <Input
                  id="birthday-day"
                  ref={dayRef}
                  aria-label={t.birthdayDay}
                  placeholder="DD"
                  maxLength={2}
                  value={parts[0]}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    handleInputChange(e, monthRef, 2);
                    onChange(`${newValue}.${parts[1]}.${parts[2]}`);
                  }}
                  className={cn(errors.birthday && "border-red-500")}
                  inputMode="numeric"
                  autoComplete="bday-day"
                  name="bday-day"
                />
              </div>
              <div className="w-16">
                <Input
                  id="birthday-month"
                  ref={monthRef}
                  aria-label={t.birthdayMonth}
                  placeholder="MM"
                  maxLength={2}
                  value={parts[1]}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    handleInputChange(e, yearRef, 2);
                    onChange(`${parts[0]}.${newValue}.${parts[2]}`);
                  }}
                  className={cn(errors.birthday && "border-red-500")}
                  inputMode="numeric"
                  autoComplete="bday-month"
                  name="bday-month"
                />
              </div>
              <div className="w-24">
                <Input
                  id="birthday-year"
                  ref={yearRef}
                  aria-label={t.birthdayYear}
                  placeholder="YYYY"
                  maxLength={4}
                  value={parts[2]}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    handleInputChange(e, null, 4);
                    onChange(`${parts[0]}.${parts[1]}.${newValue}`);
                  }}
                  className={cn(errors.birthday && "border-red-500")}
                  inputMode="numeric"
                  autoComplete="bday-year"
                  name="bday-year"
                />
              </div>
            </div>
          );
        }}
      />
      {errors.birthday?.type === "required" && (
        <span className="text-sm text-red-500">{t.birthdayRequired}</span>
      )}
      {errors.birthday?.type === "validate" && (
        <span className="text-sm text-red-500">
          {typeof errors.birthday.message === "string"
            ? errors.birthday.message
            : t.birthdayRequired}
        </span>
      )}
    </div>
  );
};
